"use client";

import dynamic from "next/dynamic";
import Link from "next/link";
import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { useCookies } from "next-client-cookies";
import {
  ShoppingCart,
  Heart,
  Menu,
  Truck,
  User,
  Home,
  Search,
} from "lucide-react";

import useCartInfo from "@/hooks/use-cart-info";
import useSticky from "@/hooks/use-sticky";
import HeaderNavigation from "./HeaderNavigation";
import styles from "./Header.module.css";

const OffCanvas = dynamic(() => import("@/components/common/offcanvas"), {
  ssr: false,
});

const MiniCart = dynamic(() => import("./header-com/mini-cart"), {
  ssr: false,
});

const TrackOrderModal = dynamic(() => import("@/utils/TrackOrderModal"), {
  ssr: false,
});

const SearchPopup = dynamic(() => import("./header-com/search-popup"), {
  ssr: false,
});

type IProps = {
  white_bg?: boolean;
};

type SearchSuggestion = {
  id: string;
  name: string;
  slug?: string;
  productCode?: string;
  product_code?: string;
  images?: Array<{
    url?: string;
    image?: string;
  }>;
};

type SearchProduct = {
  id: string | number;
  name: string;
  slug: string;
  productCode?: string | null;
  barcode?: string | null;
  images?: {
    image: string;
  }[];
};

const Header = ({ white_bg }: IProps) => {
  const cookies = useCookies();
  const router = useRouter();

  const { sticky } = useSticky();
  const { quantity } = useCartInfo();

  const [showSidebar, setShowSidebar] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showTrack, setShowTrack] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);

  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchProduct[]>([]);

  const [headerHeight, setHeaderHeight] = useState(0);

  const userDropdownRefDesktop = useRef<HTMLDivElement>(null);
  const userDropdownRefMobile = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const searchAreaRef = useRef<HTMLDivElement>(null);

  /* =======================================================
     INITIAL EFFECT
  ======================================================= */

  useEffect(() => {
    setMounted(true);

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideDesktop =
        userDropdownRefDesktop.current?.contains(target) ?? false;
      const insideMobile =
        userDropdownRefMobile.current?.contains(target) ?? false;

      if (!insideDesktop && !insideMobile) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleOutsideSearchClick = (event: MouseEvent | TouchEvent) => {
      if (!searchAreaRef.current) return;

      if (!searchAreaRef.current.contains(event.target as Node)) {
        setSearchFocused(false);
        setSearchResults([]);
      }
    };

    document.addEventListener("mousedown", handleOutsideSearchClick);
    document.addEventListener("touchstart", handleOutsideSearchClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideSearchClick);
      document.removeEventListener("touchstart", handleOutsideSearchClick);
    };
  }, []);

  /* =======================================================
     HEADER HEIGHT — ResizeObserver auto-tracks whichever
     block (mobile/desktop) CSS is currently showing, so the
     spacer always matches the real rendered height
  ======================================================= */

  useEffect(() => {
    if (!headerRef.current) return;

    const updateHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };

    updateHeight();

    const ro = new ResizeObserver(() => updateHeight());
    ro.observe(headerRef.current);

    window.addEventListener("resize", updateHeight);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, [mounted]);

  /* =======================================================
     LOGIN STATUS
  ======================================================= */

  const isLoggedIn = mounted && cookies.get("userinfo") && cookies.get("token");

  const handleCartClick = () => {
    if (quantity > 0) {
      setShowCart(!showCart);
    }
  };

  const handleLogout = () => {
    cookies.remove("userinfo");
    cookies.remove("token");
    window.location.reload();
  };

  const handleSearchSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();

    const q = searchQuery.trim();

    if (q) {
      setSuggestions([]);
      setSearchFocused(false);
      router.push(`/shop?searchText=${encodeURIComponent(q)}`);
    } else {
      router.push("/shop");
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setSearchFocused(true);
    }

    if (e.key === "Escape") {
      setSearchFocused(false);
      setSearchResults([]);
    }
  };

  useEffect(() => {
    const query = searchQuery.trim();

    if (query.length < 1) {
      setSuggestions([]);
      setSearchLoading(false);
      return;
    }

    const controller = new AbortController();

    const timer = setTimeout(async () => {
      try {
        setSearchLoading(true);

        const baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL;

        if (!baseUrl) {
          console.error("NEXT_PUBLIC_API_BASE_URL is not defined");
          setSuggestions([]);
          return;
        }

        const url =
          `${baseUrl}/customer/products` +
          `?name=${encodeURIComponent(query)}` +
          `&limit=12&page=1`;

        const response = await fetch(url, {
          method: "GET",
          signal: controller.signal,
          cache: "no-store",
        });

        if (!response.ok) {
          throw new Error(`Search API failed: ${response.status}`);
        }

        const result = await response.json();

        const products = Array.isArray(result?.data) ? result.data : [];

        setSuggestions(products);
      } catch (error: any) {
        if (error?.name !== "AbortError") {
          console.error("Search suggestion error:", error);
          setSuggestions([]);
        }
      } finally {
        setSearchLoading(false);
      }
    }, 200);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [searchQuery]);

  const handleSuggestionClick = (product: SearchSuggestion) => {
    const identifier = product?.slug || product?.id;

    if (!identifier) {
      return;
    }

    setSuggestions([]);
    setSearchFocused(false);
    setSearchQuery("");

    router.push(`/product-details/${identifier}`);
  };

  return (
    <>
      <header
        ref={headerRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          width: "100%",
          zIndex: 999,
          background: white_bg ? "#fff" : "#fafafa",
          boxShadow: sticky
            ? "0 4px 15px rgba(0,0,0,0.15)"
            : "0 2px 6px rgba(196,196,196,0.79)",
          transition: "box-shadow 0.3s ease",
          backdropFilter: sticky ? "blur(10px)" : "none",
        }}
      >
        {/* TOP BAR */}
        <div
          style={{
            maxWidth: "1400px",
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            position: "relative",
          }}
          className="hdrTopBarInner"
        >
          {/* ── MOBILE BLOCK (hidden on desktop via CSS) ── */}
          <div className="hdrMobileRow">
            {/* LEFT — MENU + GIF */}
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <button
                className={`${styles.whiteBtn} ${styles.iconBtn}`}
                onClick={() => setShowSidebar(true)}
              >
                <Menu style={{ color: "#000", width: "22px", height: "22px" }} />
              </button>

              <img
                src="/200w.gif"
                alt="delivery"
                style={{
                  width: "52px",
                  height: "52px",
                  objectFit: "contain",
                  flexShrink: 0,
                  filter: "drop-shadow(0 2px 6px rgba(255,107,53,0.25))",
                }}
              />
            </div>

            {/* CENTER — LOGO */}
            <div
              style={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Link
                href="/"
                style={{ display: "flex", alignItems: "center", gap: "2px", textDecoration: "none" }}
              >
                <img
                  src="/logo/logo.png"
                  alt="IMall"
                  style={{ width: "34px", height: "auto", objectFit: "contain" }}
                />
                <span
                  style={{
                    fontSize: "20px",
                    fontWeight: "800",
                    background: "linear-gradient(135deg,#ff6b35 0%,#f7931e 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    fontFamily: "'Poppins',sans-serif",
                    letterSpacing: "1px",
                  }}
                >
                  iMall
                </span>
              </Link>
            </div>

            {/* RIGHT — USER */}
            <div style={{ width: "44px", display: "flex", justifyContent: "flex-end" }}>
              {isLoggedIn ? (
                <div style={{ position: "relative" }} ref={userDropdownRefMobile}>
                  <button
                    className={`${styles.whiteBtn} ${styles.iconBtn}`}
                    onClick={() => setShowUserDropdown(!showUserDropdown)}
                  >
                    <User style={{ color: "#000", width: "20px", height: "20px" }} />
                  </button>

                  {showUserDropdown && (
                    <div className={styles.userDropdown}>
                      <Link href="/account">My Account</Link>
                      <Link href="/orders">My Orders</Link>
                      <button onClick={handleLogout}>Logout</button>
                    </div>
                  )}
                </div>
              ) : (
                <Link href="/login" style={{ textDecoration: "none" }}>
                  <button className={`${styles.whiteBtn} ${styles.iconBtn}`}>
                    <User style={{ color: "#000", width: "20px", height: "20px" }} />
                  </button>
                </Link>
              )}
            </div>
          </div>

          {/* ── DESKTOP BLOCK (hidden on mobile via CSS) ── */}
          <div className="hdrDesktopRow">
            {/* DESKTOP — LOGO + GIF */}
            <div style={{ display: "flex", alignItems: "center", flex: "none" }}>
              <Link
                href="/"
                style={{ display: "flex", alignItems: "center", gap: "2px", textDecoration: "none" }}
              >
                <img
                  src="/200w.gif"
                  alt="delivery"
                  style={{
                    width: "78px",
                    height: "78px",
                    objectFit: "contain",
                    flexShrink: 0,
                    filter: "drop-shadow(0 2px 6px rgba(255,107,53,0.25))",
                    transition: "transform 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateX(5px) scale(1.05)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateX(0) scale(1)";
                  }}
                />

                <img
                  src="/logo/logo.png"
                  alt="IMall"
                  style={{
                    width: "48px",
                    height: "auto",
                    objectFit: "contain",
                  }}
                />

                <span
                  style={{
                    fontSize: "27px",
                    fontWeight: "800",
                    background:
                      "linear-gradient(135deg,#ff6b35 0%,#f7931e 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    fontFamily: "'Poppins',sans-serif",
                    letterSpacing: "1px",
                  }}
                >
                  iMall
                </span>
              </Link>
            </div>

            {/* DESKTOP NAV */}
            <nav style={{ flex: 1, display: "flex", justifyContent: "center", minHeight: 24 }}>
              <HeaderNavigation />
            </nav>

            {/* DESKTOP RIGHT ICONS */}
            <div className={styles.iconGroup}>
              {/* TRACK */}
              <button
                className={`${styles.whiteBtn} ${styles.iconBtn}`}
                onClick={() => setShowTrack(true)}
              >
                <Truck style={{ color: "#333", width: "18px", height: "18px" }} />
              </button>

              {/* WISHLIST */}
              <button className={`${styles.whiteBtn} ${styles.iconBtn}`}>
                <Heart style={{ color: "#e9052f", width: "19px", height: "19px" }} />
              </button>

              {/* CART */}
              <div style={{ position: "relative" }}>
                <button
                  className={`${styles.whiteBtn} ${styles.iconBtn}`}
                  onClick={handleCartClick}
                >
                  <ShoppingCart style={{ color: "#333", width: "19px", height: "19px" }} />

                  {mounted && quantity > 0 && (
                    <span className={styles.cartBadge}>{quantity}</span>
                  )}
                </button>

                {mounted && quantity > 0 && showCart && (
                  <MiniCart
                    showCart={showCart}
                    setShowCart={setShowCart}
                    setLoading={() => {}}
                  />
                )}
              </div>

              {/* USER */}
              {isLoggedIn ? (
                <div style={{ position: "relative" }} ref={userDropdownRefDesktop}>
                  <button
                    className={`${styles.whiteBtn} ${styles.iconBtn}`}
                    onClick={() => setShowUserDropdown(!showUserDropdown)}
                  >
                    <User style={{ color: "#333", width: "18px", height: "18px" }} />
                  </button>

                  {showUserDropdown && (
                    <div className={styles.userDropdown}>
                      <Link href="/account">My Account</Link>
                      <Link href="/orders">My Orders</Link>
                      <button onClick={handleLogout}>Logout</button>
                    </div>
                  )}
                </div>
              ) : (
                <Link href="/login">
                  <button className={`${styles.whiteBtn} ${styles.iconBtn}`}>
                    <User style={{ color: "#333", width: "18px", height: "18px" }} />
                  </button>
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* SEARCH BAR */}
        <div className={styles.headerSearchBar}>
          <div className={styles.searchOrb1} />
          <div className={styles.searchOrb2} />

          <div className={styles.searchBarContent}>
            <div ref={searchAreaRef} className={styles.headerSearchForm}>
              <div
                className={`${styles.headerSearchInner}${
                  searchFocused ? ` ${styles.headerSearchFocused}` : ""
                }`}
              >
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search medicines, products, brands..."
                  className={styles.headerSearchInput}
                  autoComplete="off"
                />

                <button
                  type="button"
                  onClick={() => handleSearchSubmit()}
                  className={styles.headerSearchIconBtn}
                  aria-label="Search"
                >
                  <Search size={20} />
                </button>

                {searchFocused && searchQuery.trim().length >= 1 && (
                  <div className={styles.searchSuggestions}>
                    <div className={styles.searchSuggestionsScroll}>
                      {searchLoading ? (
                        <div className={styles.searchSuggestionLoading}>
                          Searching...
                        </div>
                      ) : suggestions.length > 0 ? (
                        suggestions.map((product) => {
                          const imageUrl = product?.images?.[0]?.image;
                          const productCode =
                            product?.productCode || product?.product_code;

                          return (
                            <button
                              key={product.id}
                              type="button"
                              className={styles.searchSuggestionItem}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleSuggestionClick(product);
                              }}
                            >
                              <div className={styles.searchSuggestionImage}>
                                {imageUrl ? (
                                  <img src={imageUrl} alt={product.name} />
                                ) : (
                                  <div className={styles.searchSuggestionPlaceholder}>
                                    <Search size={16} />
                                  </div>
                                )}
                              </div>

                              <div className={styles.searchSuggestionInfo}>
                                <span className={styles.searchSuggestionName}>
                                  {product.name}
                                </span>

                                {productCode && (
                                  <span className={styles.searchSuggestionCode}>
                                    {productCode}
                                  </span>
                                )}
                              </div>
                            </button>
                          );
                        })
                      ) : (
                        <div className={styles.searchNoResult}>
                          No medicine or product found
                        </div>
                      )}
                    </div>

                    {!searchLoading && suggestions.length > 0 && (
                      <button
                        type="button"
                        className={styles.searchViewAll}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSearchSubmit();
                        }}
                      >
                        View all results for &quot;{searchQuery.trim()}&quot;
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className={styles.searchTagline}>
              <span className={styles.searchTaglineSep}>·</span>
              <span>Genuine Products with Express Delivery</span>
            </div>
          </div>
        </div>
      </header>

      {/* HEADER SPACER */}
      <div style={{ height: headerHeight }} />

      {/* MOBILE BOTTOM NAV — CSS-controlled, no JS flash */}
      <div className="hdrMobileBottomNavWrap">
        <div className={styles.mobileBottomNav}>
          <div className={styles.navGroup}>
            <button onClick={() => setShowSidebar(true)} className={styles.navButton}>
              <Menu size={20} />
              <span>Category</span>
            </button>

            <button className={styles.navButton} onClick={() => setShowTrack(true)}>
              <Truck size={20} />
              <span>Track</span>
            </button>
          </div>

          <Link href="/" className={styles.homeBtn}>
            <div className={styles.homeCircle}>
              <Home size={26} />
            </div>
            <span>Home</span>
          </Link>

          <div className={styles.navGroup}>
            <button onClick={() => router.push("/cart")} className={styles.navButton}>
              <ShoppingCart size={20} />
              <span>Cart</span>
              {quantity > 0 && <span className={styles.cartCount}>{quantity}</span>}
            </button>

            {isLoggedIn ? (
              <Link href="/account" className={styles.navButton}>
                <User size={20} />
                <span>Account</span>
              </Link>
            ) : (
              <Link href="/login" className={styles.navButton}>
                <User size={20} />
                <span>Login</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* DYNAMIC COMPONENTS */}
      {mounted && (
        <>
          <SearchPopup showSearch={showSearch} setShowSearch={setShowSearch} />
          <TrackOrderModal show={showTrack} setShow={setShowTrack} />
          <OffCanvas openMobileMenus={showSidebar} setOpenMobileMenus={setShowSidebar} />
        </>
      )}

      {/* ── RESPONSIVE SWITCH — pure CSS, applies instantly on paint,
          no JS delay, so no mobile/desktop flash on load ── */}
      <style jsx>{`
        .hdrTopBarInner {
          padding: 4px 14px;
        }
        .hdrMobileRow {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          position: relative;
        }
        .hdrDesktopRow {
          display: none;
        }
        .hdrMobileBottomNavWrap {
          display: block;
        }

        @media (min-width: 1024px) {
          .hdrTopBarInner {
            padding: 10px 40px;
          }
          .hdrMobileRow {
            display: none;
          }
          .hdrDesktopRow {
            display: flex;
            align-items: center;
            width: 100%;
          }
          .hdrMobileBottomNavWrap {
            display: none;
          }
        }
      `}</style>
    </>
  );
};

export default Header;